// Client Contacts tab. List + inline "+ Contact" form. Ported from the
// original ClientDetailPage Contacts <TabsContent> block (PR #98). The create
// form itself now lives in the shared <ContactForm> component (2026-07-23) so
// the presentation send window's "Add this person as a contact" popup reuses
// the identical fields — one form, no fork.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useOutletContext } from "react-router-dom";
import { Mail, Phone, Plus } from "lucide-react";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { ContactForm } from "@/components/contacts/ContactForm";
import { isBuildingEmail } from "@merchd/shared";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";

interface ContactRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  isPrimary: boolean | null;
  tags?: string[] | null;
  /** Set when this person's home is another location of the company and
   *  they're linked to this one (multi-location contacts). */
  linkedFrom?: { clientId: string; name: string } | null;
}

export function ClientContactsTab() {
  const { canEdit, clientId } = useOutletContext<ClientDetailContext>();

  const { data, isLoading } = useQuery({
    queryKey: ["clients", clientId, "contacts"],
    queryFn: () =>
      api<{ contacts: ContactRow[] }>(`/clients/${clientId}/contacts`),
    enabled: clientId.length > 0,
  });

  const contacts = data?.contacts ?? [];

  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const editing = contacts.find((c) => c.id === editingId) ?? null;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Contacts <span className="font-normal text-muted">({contacts.length})</span>
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            People you work with here. A contact is not a portal login — portal access is invited separately.
          </p>
        </div>
        {canEdit && !isCreating && (
          <Button variant="outline" onClick={() => { setEditingId(null); setIsCreating(true); }}>
            <Plus className="h-4 w-4" aria-hidden /> Add contact
          </Button>
        )}
      </div>

      {isCreating && (
        <div className="mb-4">
          <ContactForm
            clientId={clientId}
            onCreated={() => setIsCreating(false)}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      )}

      {editing && (
        <div className="mb-4">
          <ContactForm
            key={editing.id}
            clientId={clientId}
            contact={editing}
            onCreated={() => setEditingId(null)}
            onCancel={() => setEditingId(null)}
          />
        </div>
      )}

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading contacts" />
      ) : contacts.length === 0 ? (
        !isCreating && (
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
            <p className="font-medium text-ink">No contacts yet.</p>
            <p className="mt-1 text-sm text-muted">Add the people who order and approve for this account.</p>
          </div>
        )
      ) : (
        <div className={TABLE_WRAP} role="region" aria-label="Contacts" tabIndex={0}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Name</th>
                <th scope="col" className={TH}>Email</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Phone</th>
                {canEdit && <th scope="col" className={TH}><span className="sr-only">Edit</span></th>}
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {contacts.map((ct) => (
                <tr key={ct.id} className={TROW}>
                  <td className={TD}>
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">
                        {[ct.firstName, ct.lastName].filter(Boolean).join(" ") || (isBuildingEmail(ct.tags) ? ct.email : "—")}
                      </span>
                      {isBuildingEmail(ct.tags) && <StatusPill tone="info">Building email</StatusPill>}
                      {ct.isPrimary && <StatusPill tone="active">Primary</StatusPill>}
                    </span>
                    {ct.linkedFrom && (
                      <span className="block text-[11px] text-muted">
                        Also works here · based at{" "}
                        <Link to={`/clients/${ct.linkedFrom.clientId}/contacts`} className="font-medium text-ink hover:underline">
                          {ct.linkedFrom.name}
                        </Link>
                      </span>
                    )}
                    {ct.title && <span className="block text-[11px] text-muted">{ct.title}</span>}
                    {ct.phone && (
                      <a href={`tel:${ct.phone}`} className="mt-0.5 block text-[11px] text-muted hover:text-ink sm:hidden">
                        {ct.phone}
                      </a>
                    )}
                  </td>
                  <td className={TD}>
                    {ct.email ? (
                      <a href={`mailto:${ct.email}`} className="inline-flex items-center gap-1.5 text-ink hover:underline">
                        <Mail className="h-3.5 w-3.5 text-muted" aria-hidden />
                        <span className="break-all">{ct.email}</span>
                      </a>
                    ) : (
                      <span className="text-muted">No email — can't receive documents</span>
                    )}
                  </td>
                  <td className={`${TD} hidden sm:table-cell`}>
                    {ct.phone ? (
                      <a href={`tel:${ct.phone}`} className="inline-flex items-center gap-1.5 text-ink hover:underline">
                        <Phone className="h-3.5 w-3.5 text-muted" aria-hidden />
                        {ct.phone}
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className={`${TD} text-right`}>
                      {ct.linkedFrom ? (
                        // Edited where they're based — that's where their
                        // details and locations live.
                        <Link
                          to={`/clients/${ct.linkedFrom.clientId}/contacts`}
                          className="text-sm font-semibold text-ink hover:underline"
                          aria-label={`Edit ${[ct.firstName, ct.lastName].filter(Boolean).join(" ")} at ${ct.linkedFrom.name}`}
                        >
                          Edit at {ct.linkedFrom.name}
                        </Link>
                      ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreating(false);
                          setEditingId(ct.id);
                        }}
                        className="text-sm font-semibold text-ink hover:underline"
                        aria-label={`Edit ${[ct.firstName, ct.lastName].filter(Boolean).join(" ")}`}
                      >
                        Edit
                      </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
